'use client'

/**
 * Hand-rolled conversion-path Sankey — shared by logged-in and example views.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { FlowConnectionIcon } from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'
import {
  colorForLink,
  colorForNode,
  type ConversionFlowData,
  type FlowNode,
} from '@/lib/traffic/conversion-example'

const H = 540
const NODE_W = 15
const MARGIN_Y = 28
const GAP = 14
const MARGIN_X = 12
/** Room for the outcome-column labels on the right edge. */
const LABEL_RESERVE = 200
const MIN_WIDTH = 640

function columnPositions(width: number, count: number): number[] {
  const col0 = MARGIN_X
  const col2 = Math.max(col0 + 240, width - LABEL_RESERVE - NODE_W)
  if (count <= 1) return [col0]
  return Array.from({ length: count }, (_, index) => (
    col0 + ((col2 - col0) * index) / (count - 1)
  ))
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1000 === 0 ? 0 : 1)}K`
  return n.toLocaleString()
}

interface LaidOutNode extends FlowNode {
  x: number
  y: number
  h: number
  value: number
}

interface LaidOutRibbon {
  key: string
  path: string
  color: string
  source: string
  target: string
  sourceName: string
  targetName: string
  value: number
  pct: number
  pctLabel: string
  midX: number
  midY: number
  sliceH: number
}

function useLayout(data: ConversionFlowData, width: number) {
  return useMemo(() => {
    const { nodes, links } = data
    const nodeById = new Map(nodes.map((n) => [n.id, n]))
    const columnCount = Math.max(3, ...nodes.map((n) => n.column + 1))
    const columnX = columnPositions(width, columnCount)

    const io = new Map<string, { in: number; out: number }>()
    nodes.forEach((n) => io.set(n.id, { in: 0, out: 0 }))
    links.forEach((l) => {
      io.get(l.source)!.out += l.value
      io.get(l.target)!.in += l.value
    })

    const usableH = H - 2 * MARGIN_Y
    const columns: FlowNode[][] = Array.from({ length: columnCount }, () => [])
    nodes.forEach((n) => columns[n.column].push(n))
    const colTotal = columns.map((col) =>
      col.reduce((s, n) => s + Math.max(io.get(n.id)!.in, io.get(n.id)!.out), 0),
    )
    const maxGaps = Math.max(...columns.map((c) => c.length - 1), 0)
    const grandTotal = Math.max(...colTotal, 1)
    const pxPerUnit = (usableH - maxGaps * GAP) / grandTotal

    const laidOut = new Map<string, LaidOutNode>()
    columns.forEach((col, ci) => {
      const totalBar = colTotal[ci] * pxPerUnit
      const gaps = (col.length - 1) * GAP
      let y = MARGIN_Y + (usableH - totalBar - gaps) / 2
      col.forEach((n) => {
        const value = Math.max(io.get(n.id)!.in, io.get(n.id)!.out)
        const h = value * pxPerUnit
        laidOut.set(n.id, { ...n, x: columnX[ci], y, h, value })
        y += h + GAP
      })
    })

    const outCursor = new Map<string, number>()
    const inCursor = new Map<string, number>()
    const ribbons: LaidOutRibbon[] = links.map((l, i) => {
      const s = laidOut.get(l.source)!
      const t = laidOut.get(l.target)!
      const sliceH = l.value * pxPerUnit
      const so = outCursor.get(l.source) ?? 0
      const ti = inCursor.get(l.target) ?? 0
      outCursor.set(l.source, so + sliceH)
      inCursor.set(l.target, ti + sliceH)
      const sy0 = s.y + so
      const sy1 = sy0 + sliceH
      const ty0 = t.y + ti
      const ty1 = ty0 + sliceH
      const x0 = s.x + NODE_W
      const x1 = t.x
      const c = (x0 + x1) / 2
      const path = `M${x0},${sy0} C${c},${sy0} ${c},${ty0} ${x1},${ty0} L${x1},${ty1} C${c},${ty1} ${c},${sy1} ${x0},${sy1} Z`
      return {
        key: `${l.source}-${l.target}-${i}`,
        path,
        color: colorForLink(l, nodeById),
        source: l.source,
        target: l.target,
        sourceName: s.name,
        targetName: t.name,
        value: l.value,
        pct: l.pct,
        pctLabel: l.pctLabel,
        midX: c,
        midY: (sy0 + sy1 + ty0 + ty1) / 4,
        sliceH,
      }
    })

    return { nodes: [...laidOut.values()], ribbons, viewWidth: width }
  }, [data, width])
}

interface Props {
  data: ConversionFlowData
  legend: Array<{ label: string; color: string }>
  loading?: boolean
  metric?: 'sessions' | 'revenue'
}

function fmtValue(n: number, metric: 'sessions' | 'revenue'): string {
  if (metric === 'revenue') {
    const value = n / 100
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `$${(value / 1_000).toFixed(value % 1000 === 0 ? 0 : 1)}K`
    return `$${Math.round(value).toLocaleString()}`
  }
  return fmt(n)
}

export function ConversionPathsSankey({ data, legend, loading, metric = 'sessions' }: Props) {
  const [hovered, setHovered] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(MIN_WIDTH)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const next = el.clientWidth
      if (next > 0) setWidth(Math.max(next, MIN_WIDTH))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { nodes, ribbons, viewWidth } = useLayout(data, width)

  const ribbonOpacity = (r: LaidOutRibbon): number => {
    if (!hovered) return 0.46
    return r.source === hovered || r.target === hovered ? 0.82 : 0.08
  }

  const hasData = data.nodes.length > 0 && data.links.length > 0

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-text-strong">
          <HugeiconsIcon icon={FlowConnectionIcon} className="size-4 text-[#0A8A6E]" />
          Channel → Site visitors → Purchase
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#939294] sm:hidden">
          swipe to explore →
        </span>
      </div>

      <div
        ref={containerRef}
        className={cn(
          'relative w-full overflow-x-auto [scrollbar-width:thin]',
          loading && 'opacity-60',
        )}
      >
        {hasData ? (
          <svg
            viewBox={`0 0 ${viewWidth} ${H}`}
            width="100%"
            className="block h-auto min-h-[360px] w-full"
            role="img"
            aria-label="Conversion path flow from channels through site visitors to purchase"
            onMouseLeave={() => setHovered(null)}
          >
            <g>
              {ribbons.map((r) => (
                <path
                  key={r.key}
                  d={r.path}
                  fill={r.color}
                  opacity={ribbonOpacity(r)}
                  style={{ transition: 'opacity 140ms ease' }}
                  onMouseEnter={() => setHovered(r.source)}
                >
                  <title>
                    {r.sourceName} → {r.targetName}: {fmtValue(r.value, metric)} ({r.pct}% {r.pctLabel})
                  </title>
                </path>
              ))}
            </g>

            <g>
              {ribbons.map((r) =>
                r.sliceH >= 11 ? (
                  <text
                    key={`${r.key}-pct`}
                    x={r.midX}
                    y={r.midY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={11}
                    fontWeight={700}
                    opacity={hovered && r.source !== hovered && r.target !== hovered ? 0.1 : 1}
                    style={{
                      fill: 'var(--color-atrium-black)',
                      paintOrder: 'stroke',
                      stroke: 'var(--color-canvas)',
                      strokeWidth: 3,
                      strokeLinejoin: 'round',
                      pointerEvents: 'none',
                      transition: 'opacity 140ms ease',
                    }}
                  >
                    {r.pct}%
                  </text>
                ) : null,
              )}
            </g>

            <g>
              {nodes.map((n) => {
                const color = colorForNode(n)
                const labelX = n.x + NODE_W + 9
                return (
                  <g
                    key={n.id}
                    onMouseEnter={() => setHovered(n.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={n.x}
                      y={n.y}
                      width={NODE_W}
                      height={Math.max(2, n.h)}
                      rx={4}
                      fill={color}
                      opacity={hovered && hovered !== n.id ? 0.4 : 1}
                      style={{ transition: 'opacity 140ms ease' }}
                    />
                    <text
                      x={labelX}
                      y={n.y + n.h / 2 + 2}
                      textAnchor="start"
                      fontSize={13}
                      fontWeight={700}
                      style={{
                        fill: 'var(--color-atrium-black)',
                        paintOrder: 'stroke',
                        stroke: 'var(--color-canvas)',
                        strokeWidth: 3,
                        strokeLinejoin: 'round',
                        pointerEvents: 'none',
                      }}
                    >
                      {n.name}
                    </text>
                    <text
                      x={labelX}
                      y={n.y + n.h / 2 + 17}
                      textAnchor="start"
                      fontSize={11}
                      fontWeight={500}
                      style={{
                        fill: 'var(--color-ink-3)',
                        paintOrder: 'stroke',
                        stroke: 'var(--color-canvas)',
                        strokeWidth: 3,
                        strokeLinejoin: 'round',
                        pointerEvents: 'none',
                      }}
                    >
                      {fmtValue(n.value, metric)}
                      {n.sub ? ` · ${n.sub}` : ''}
                    </text>
                  </g>
                )
              })}
            </g>
          </svg>
        ) : (
          <div className="flex min-h-[360px] items-center justify-center px-6">
            <div className="max-w-md text-center">
              <h2 className="text-sm font-semibold text-text-strong">
                No traffic recorded in this window
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-text">
                Once your tag fires and campaigns drive clicks, the full journey from channel to
                conversion shows up here.
              </p>
            </div>
          </div>
        )}
      </div>

      {legend.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-black/[0.06] px-1 pt-3 dark:border-white/[0.06]">
          {legend.map((l) => (
            <span
              key={l.label}
              className="flex items-center gap-1.5 text-[11.5px] font-medium text-text"
            >
              <span className="size-2.5 rounded-full" style={{ background: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
